import React, { useState } from 'react';
import { CommentData } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import Avatar from './Avatar';
import TimecodeDisplay from './TimecodeDisplay';
import { EditIcon, TrashIcon, ReplyIcon, AttachFileIcon } from './playerIcons';

interface CommentProps {
  comment: CommentData;
  depth?: number;
  onSeek: (time: number) => void;
  canDelete: boolean;
  canEdit: boolean;
  onDelete: () => void;
  onEdit?: (commentId: string, newText: string) => void;
  onJumpAndShow: () => void;
  onReply?: () => void;
  fps?: number;
}

const Comment: React.FC<CommentProps> = ({
  comment,
  depth = 0,
  onSeek,
  canDelete,
  canEdit,
  onDelete,
  onEdit,
  onJumpAndShow,
  onReply,
  fps = 24,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const isVideoComment = !!comment.recordingUrl;
  const indent = Math.min(depth, 4) * 12;

  const handleSaveEdit = () => {
    if (onEdit && editText.trim()) {
      onEdit(comment.id, editText.trim());
      setIsEditing(false);
    }
  };

  return (
    <div
      id={`comment-${comment.id}`}
      className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/40 hover:border-gray-600/60 transition-colors"
      style={{ marginLeft: indent }}
    >
      <div className="flex items-start gap-2">
        <Avatar name={comment.authorName || 'User'} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white truncate">
              {comment.authorName || 'Unknown'}
            </span>
            <TimecodeDisplay
              timecode={comment.timecode}
              timeInSeconds={comment.timeInSeconds}
              fps={fps}
              onClick={() => {
                onSeek(comment.timeInSeconds);
                onJumpAndShow();
              }}
            />
          </div>

          {isEditing ? (
            <div className="mt-2 space-y-2">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full bg-gray-800 text-gray-200 rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="px-2 py-1 text-xs bg-indigo-600 text-white rounded"
                >
                  {t('player.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditText(comment.text);
                  }}
                  className="px-2 py-1 text-xs text-gray-400 hover:text-white"
                >
                  {t('player.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-1 text-sm text-gray-300 whitespace-pre-wrap break-words">{comment.text}</p>
          )}

          {(comment.frameThumbnailUrl || comment.drawingDataUrl) && (
            <button type="button" onClick={onJumpAndShow} className="mt-2 block">
              <img
                src={comment.frameThumbnailUrl || comment.drawingDataUrl}
                alt="Frame"
                className="max-h-24 rounded border border-gray-700 hover:border-indigo-500 transition-colors bg-black"
              />
            </button>
          )}

          {isVideoComment && comment.recordingUrl && (
            <div className="mt-2 flex items-center gap-2">
              <audio src={comment.recordingUrl} controls className="h-8 max-w-full" />
              {(comment.duration || comment.recordingDuration) && (
                <span className="text-xs text-gray-500">
                  {((comment.duration || comment.recordingDuration) as number).toFixed(1)}s
                </span>
              )}
            </div>
          )}

          {comment.attachmentUrl && (
            <a
              href={comment.attachmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"
            >
              <AttachFileIcon className="w-3.5 h-3.5" />
              {comment.attachmentOriginalName || 'Attachment'}
            </a>
          )}

          <div className="mt-2 flex items-center gap-1">
            {onReply && depth < 4 && (
              <button
                type="button"
                onClick={onReply}
                className="p-1.5 text-gray-500 hover:text-indigo-400 rounded"
                title={t('player.reply')}
              >
                <ReplyIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {canEdit && onEdit && !isEditing && (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="p-1.5 text-gray-500 hover:text-indigo-400 rounded"
                title={t('player.edit')}
              >
                <EditIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="p-1.5 text-gray-500 hover:text-red-400 rounded"
                title={t('player.delete')}
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Comment;
